import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { HelpCircle, Plus } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type {
  EmployeeEntitlementSummaryRow,
  LeaveEntitlementListItem,
} from "@/modules/hr/leave/domain/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { useLeaveEntitlementsQuery, useLeaveTypesQuery } from "../api/queries";
import {
  LeaveEntitlementsTour,
  useLeaveEntitlementsTour,
} from "../tour/LeaveEntitlementsTour";
import { LeaveEntitlementsByEmployeeTable } from "../ui/LeaveEntitlementsByEmployeeTable";
import {
  LeaveEntitlementsFilter,
  type LeaveEntitlementsSearchParams,
} from "../ui/LeaveEntitlementsFilter";

const CURRENT_YEAR = new Date().getFullYear();
const EMPLOYEE_PAGE_SIZE = 20;

function groupByEmployee(
  rows: LeaveEntitlementListItem[],
): EmployeeEntitlementSummaryRow[] {
  const byEmployee = new Map<
    string,
    {
      name: string;
      email: string;
      year: number;
      rows: LeaveEntitlementListItem[];
    }
  >();
  for (const r of rows) {
    const existing = byEmployee.get(r.employeeId);
    if (!existing) {
      byEmployee.set(r.employeeId, {
        name: r.employeeName,
        email: r.employeeEmail,
        year: r.year,
        rows: [r],
      });
    } else {
      existing.rows.push(r);
    }
  }
  const result: EmployeeEntitlementSummaryRow[] = [];
  byEmployee.forEach((v, employeeId) => {
    const totalDaysCanLeave = v.rows.reduce(
      (sum, e) => sum + e.totalDays + e.carriedOverDays,
      0,
    );
    const totalDaysUsed = v.rows.reduce((sum, e) => sum + e.usedDays, 0);
    const totalRemaining = v.rows.reduce((sum, e) => sum + e.remainingDays, 0);
    result.push({
      employeeId,
      employeeName: v.name,
      employeeEmail: v.email,
      year: v.year,
      totalDaysCanLeave,
      totalDaysUsed,
      totalRemaining,
      entitlements: v.rows,
    });
  });
  return result;
}

function sortSummaryRows(
  rows: EmployeeEntitlementSummaryRow[],
  sortBy: string,
  sortOrder: "asc" | "desc",
): EmployeeEntitlementSummaryRow[] {
  const dir = sortOrder === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = (a as Record<string, unknown>)[sortBy] as string | number | null;
    const bv = (b as Record<string, unknown>)[sortBy] as string | number | null;
    if (av == null && bv == null) return 0;
    if (av == null) return dir;
    if (bv == null) return -dir;
    if (typeof av === "string" && typeof bv === "string")
      return dir * av.localeCompare(bv);
    if (typeof av === "number" && typeof bv === "number")
      return dir * (av - bv);
    return dir * String(av).localeCompare(String(bv));
  });
}

export function LeaveEntitlementsPage() {
  const nav = useNavigate({ from: "/app/hr/leave/entitlements" });
  const { has } = usePermissions();
  const canCreate = has(Permissions.hr_leave.entitlements_create);
  const search = useSearch({
    from: "/app/hr/leave/entitlements",
  }) as LeaveEntitlementsSearchParams & { offset?: number; limit?: number };

  const year = search.year ?? CURRENT_YEAR;
  const sortBy = search.sort?.[0]?.field ?? "employeeName";
  const sortOrder = (search.sort?.[0]?.dir ?? "asc") as "asc" | "desc";

  const typesQuery = useLeaveTypesQuery({ isActive: true, limit: 100 });
  const leaveTypes = typesQuery.data?.data ?? [];

  const query = useMemo(
    () => ({
      offset: 0,
      limit: 500,
      year,
      leaveTypeId: search.leaveTypeId,
      search: search.search,
    }),
    [year, search.leaveTypeId, search.search],
  );

  const listQuery = useLeaveEntitlementsQuery(query);
  const rawRows = listQuery.data?.data ?? [];
  const grouped = useMemo(() => groupByEmployee(rawRows), [rawRows]);
  const sorted = useMemo(
    () => sortSummaryRows(grouped, sortBy, sortOrder),
    [grouped, sortBy, sortOrder],
  );

  const pageOffset = search.offset ?? 0;
  const pageLimit = search.limit ?? EMPLOYEE_PAGE_SIZE;
  const paginatedRows = useMemo(
    () => sorted.slice(pageOffset, pageOffset + pageLimit),
    [sorted, pageOffset, pageLimit],
  );
  const totalCount = sorted.length;

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    nav({
      search: {
        offset: newOffset,
        limit: newLimit,
        sort: search.sort,
        year,
        leaveTypeId: search.leaveTypeId,
        search: search.search,
      },
    });
  };

  const handleViewDetail = (row: EmployeeEntitlementSummaryRow) => {
    nav({
      to: "/app/hr/leave/entitlements/employee/$employeeId",
      params: { employeeId: row.employeeId },
      search: { year: row.year },
    });
  };

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: {
          offset: 0,
          limit: search.limit ?? EMPLOYEE_PAGE_SIZE,
          sort: [{ field: id, dir: (desc ? "desc" : "asc") as "asc" | "desc" }],
          year,
          leaveTypeId: search.leaveTypeId,
          search: search.search,
        },
      });
    },
    [nav, search.limit, year, search.leaveTypeId, search.search],
  );

  const tour = useLeaveEntitlementsTour();

  return (
    <>
      <Header />
      <Main>
        <LeaveEntitlementsTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between"
          data-tourid="leave-entitlements-toolbar"
        >
          <div className="flex items-center gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-2xl">ສິດລາຕໍ່ປີ</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                  title="ເລີ່ມທົດສອບການນຳທາງ"
                  aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                ຈັດສິດລາຕໍ່ພະນັກງານຕໍ່ປີ — ເລືອກປີ ແລະ ເບິ່ງລາຍລະອຽດຕໍ່ພະນັກງານ
              </p>
            </div>
          </div>
          {canCreate && (
            <Button
              variant="default"
              onClick={() => nav({ to: "/app/hr/leave/entitlements/new" })}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              ສ້າງສິດລາ (ພະນັກງານໃໝ່)
            </Button>
          )}
        </div>

        <div
          className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
          data-tourid="leave-entitlements-filter"
        >
          <LeaveEntitlementsFilter
            searchParams={{ ...search, year: search.year ?? CURRENT_YEAR }}
            fromRoute="/app/hr/leave/entitlements"
            leaveTypes={leaveTypes}
          />
          <div data-tourid="leave-entitlements-table">
            <LeaveEntitlementsByEmployeeTable
              data={paginatedRows}
              isLoading={listQuery.isLoading}
              offset={pageOffset}
              limit={pageLimit}
              totalCount={totalCount}
              onPaginationChange={handlePaginationChange}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortingChange={handleSortingChange}
              onViewDetail={handleViewDetail}
            />
          </div>
        </div>
      </Main>
    </>
  );
}
